/**
 * Weekly digest — the hero of both cloud tabs.
 *
 * Last full Mon–Sun week predicts month-end better than last month's total, so
 * this panel leads: week total + WoW, the daily shape, top services with their
 * own WoW deltas, and the pace projection those days imply.
 *
 * Provider-agnostic: it only needs the week shape both `awsWeekly` and
 * `gcpWeekly` return.
 */

import { useTranslation } from 'react-i18next';
import { CalendarDays } from 'lucide-react';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Card } from '@/components/ui/card';
import type { FinopsDailyPoint, FinopsServiceRow } from '../../../../shared/types';
import {
  DeltaBadge, EmptyState, daysInMonth, deltaOf, fmtDay, fmtPct, fmtUSD, fmtUSDCents, fmtWeekRange,
} from './shared';

/** Structural shape shared by `FinopsWeekBreakdown` and its GCP variant. */
export interface WeekLike {
  period: string;
  services: FinopsServiceRow[];
  daily: FinopsDailyPoint[];
  total: number;
  priorTotal: number;
}

const TOP_SERVICES = 8;

const DailyTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border bg-background px-3 py-2 shadow-md text-sm">
      <p className="font-medium">{fmtDay(p.date)}</p>
      <p className="text-muted-foreground">{fmtUSDCents(p.cost)}</p>
    </div>
  );
};

export function WeeklyDigestPanel({
  week,
  priorWeek,
  emptyDescription,
}: {
  week?: WeekLike;
  priorWeek?: WeekLike;
  emptyDescription?: string;
}) {
  const { t } = useTranslation();

  if (!week || (week.total === 0 && week.daily.length === 0)) {
    return (
      <EmptyState
        title={t('finops.cloud.digest_empty_title')}
        description={emptyDescription ?? t('finops.cloud.digest_empty_description')}
      />
    );
  }

  const wow = deltaOf(week.total, week.priorTotal);

  // The week's own daily average is the pace driver — not the calendar month to
  // date, which mixes in older, cheaper days.
  const dailyAvg = week.total / 7;
  const pace = dailyAvg * daysInMonth(new Date());

  const priorByService = new Map(priorWeek?.services.map((s) => [s.service, s.cost]) ?? []);
  const top = week.services.slice(0, TOP_SERVICES).map((s) => ({
    ...s,
    priorCost: priorByService.get(s.service),
    delta: priorWeek ? deltaOf(s.cost, priorByService.get(s.service) ?? 0) : null,
    share: week.total > 0 ? (s.cost / week.total) * 100 : 0,
  }));

  return (
    <Card className="p-5 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            {t('finops.cloud.digest_title')}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">{fmtWeekRange(week.period)}</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold">{fmtUSD(week.total)}</div>
          <div className="text-xs mt-0.5">
            <DeltaBadge value={wow} />{' '}
            <span className="text-muted-foreground">{t('finops.cloud.digest_wow')}</span>
          </div>
        </div>
      </div>

      <div>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={week.daily} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={(v) => `$${Math.round(v).toLocaleString('en-US')}`} tick={{ fontSize: 11 }} width={64} />
            <Tooltip content={<DailyTooltip />} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }} />
            <Bar dataKey="cost" fill="#1CC9B9" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        {/* A run-rate, not a forecast: it can differ from the projection card (06/10/2026 —
            last week ran 12% above the one before: pace 13.1k vs CE forecast 12.4k). */}
        <p className="text-xs text-muted-foreground mt-2" title={t('finops.cloud.digest_pace_hint')}>
          {t('finops.cloud.digest_pace', { avg: fmtUSD(dailyAvg), pace: fmtUSD(pace) })}
        </p>
      </div>

      <div>
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
          {t('finops.cloud.digest_top_services')}
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground border-b">
              <tr>
                <th className="py-2 pr-3">{t('finops.cloud.col_service')}</th>
                <th className="pr-3 text-right">{t('finops.cloud.col_cost')}</th>
                <th className="pr-3 text-right">{t('finops.cloud.col_share')}</th>
                <th className="pr-3 text-right">{t('finops.cloud.col_wow')}</th>
              </tr>
            </thead>
            <tbody>
              {top.map((s) => (
                <tr key={s.service} className="border-b last:border-0">
                  <td className="py-2 pr-3 font-medium">{s.service}</td>
                  <td className="pr-3 text-right tabular-nums">{fmtUSDCents(s.cost)}</td>
                  <td className="pr-3 text-right tabular-nums text-muted-foreground">{fmtPct(s.share)}</td>
                  <td className="pr-3 text-right tabular-nums"><DeltaBadge value={s.delta} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}
